import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AppTopBar, formatIDR } from "@/components/AppTopBar";
import { RupiahInput } from "@/components/RupiahInput";

// Show the ledger amount without silently rounding the original payable up.
const formatExactIDR = (value) => "Rp " + Number(value || 0).toLocaleString("id-ID", {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
});

export default function PurchaseDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [supplier, setSupplier] = useState(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [pay, setPay] = useState(null);
  const [settlement, setSettlement] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [showPayForm, setShowPayForm] = useState(false);
  const [payForm, setPayForm] = useState({ amount: "", account_id: "", reference: "" });
  const [replForm, setReplForm] = useState({ open: false, purchase_item_id: "", sku: "", max: 0, qty: "" });
  const [rev, setRev] = useState({ open: false, preview: null, reason: "", busy: false });
  const [reversal, setReversal] = useState(null);

  const openReversal = () => {
    setRev({ open: true, preview: null, reason: "", busy: false });
    api.get(`/purchases/${id}/reversal-preview`).then(({ data }) => setRev((r) => ({ ...r, preview: data })))
      .catch((err) => setError(formatApiErrorDetail(err.response?.data?.detail) || err.message));
  };
  const confirmReversal = () => {
    if (!rev.reason.trim() || !window.confirm(`Reverse Purchase ${p.purchase_no}? Seluruh efek receiving akan dibalik dan Purchase menjadi REVERSED (tidak bisa dibatalkan).`)) return;
    setRev((r) => ({ ...r, busy: true }));
    const key = (window.crypto?.randomUUID ? window.crypto.randomUUID() : `rev-${Date.now()}`).replace(/-/g, "");
    api.post(`/purchases/${id}/reverse`, { reason: rev.reason.trim(), idempotency_key: key })
      .then(() => { setNotice("Purchase berhasil di-REVERSE."); setRev({ open: false, preview: null, reason: "", busy: false }); load(); })
      .catch((err) => { const d = err.response?.data?.detail; setError(typeof d === "object" && d?.blockers ? d.blockers.map((b) => `${b.code}: ${b.message}`).join(" | ") : (formatApiErrorDetail(d) || err.message)); setRev((r) => ({ ...r, busy: false })); });
  };

  const load = () => {
    api.get(`/purchases/${id}`)
      .then(({ data }) => {
        setP(data);
        if (data.supplier_id) {
          api.get(`/suppliers/${data.supplier_id}`)
            .then(({ data: supplierData }) => setSupplier(supplierData))
            .catch(() => setSupplier(null));
        } else {
          setSupplier(null);
        }
      })
      .catch((err) => setError(formatApiErrorDetail(err.response?.data?.detail) || err.message));
    api.get(`/purchases/${id}/payments`).then(({ data }) => setPay(data)).catch(() => {});
    api.get(`/purchases/${id}/reversal`).then(({ data }) => setReversal(data.reversal)).catch(() => setReversal(null));
    api.get(`/supplier-deposits/allocations/purchase-summary/${id}`)
      .then(({ data }) => setSettlement(data)).catch(() => setSettlement(null));
    api.get(`/accounts`).then(({ data }) => setAccounts(data)).catch(() => {});
  };

  // load intentionally tracks the route id; the function is recreated on each render.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [id]);

  const recordPayment = async () => {
    setError("");
    const amount = parseFloat(payForm.amount || "0");
    if (amount <= 0) { setError("Nominal pembayaran harus lebih dari 0."); return; }
    if (!payForm.account_id) { setError("Pilih account sumber dana."); return; }
    const exactDue = Math.max(0, Number(paySummary?.purchase_total || 0) - Number(paySummary?.total_settled || 0));
    const maxCash = Math.round(exactDue);
    if (amount > maxCash) { setError(`Nominal melampaui pelunasan rupiah Rp ${maxCash.toLocaleString("id-ID")}. Nilai desimal Purchase tetap tersimpan untuk akuntansi.`); return; }
    try {
      const { data } = await api.post(`/purchases/${id}/payments`, {
        amount, account_id: payForm.account_id, reference: payForm.reference || null,
        idempotency_key: `PAY-${id}-${Date.now()}`,
      });
      setPay(data);
      setShowPayForm(false);
      setPayForm({ amount: "", account_id: "", reference: "" });
      setNotice("Pembayaran tercatat (dana MODAL).");
      load();
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  const reversePayment = async (paymentId) => {
    const reason = window.prompt("Alasan pembatalan pembayaran ini:");
    if (!reason || !reason.trim()) return;
    setError("");
    try {
      const { data } = await api.post(`/purchases/${id}/payments/${paymentId}/reverse`, { reason: reason.trim() });
      setPay(data);
      setNotice("Pembayaran dibatalkan (MODAL & sisa tagihan dipulihkan).");
      load();
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  const openPurchase = async () => {
    setError("");
    try {
      await api.patch(`/purchases/${id}/open`);
      setNotice("Purchase diubah menjadi OPEN.");
      load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const submitReplacement = async () => {
    setError("");
    const qty = parseInt(replForm.qty || "0", 10);
    if (qty <= 0) { setError("Qty barang pengganti harus lebih dari 0."); return; }
    if (qty > replForm.max) { setError(`Qty melebihi Replacement Pending (${replForm.max}).`); return; }
    try {
      await api.post(`/purchases/${id}/replacement-receipts`, {
        idempotency_key: `REPL-${replForm.purchase_item_id.slice(0, 8)}-${Date.now()}`,
        items: [{ purchase_item_id: replForm.purchase_item_id, qty }],
      });
      setReplForm({ open: false, purchase_item_id: "", sku: "", max: 0, qty: "" });
      setNotice("Barang pengganti diterima (GOOD bertambah, tanpa menambah tagihan).");
      load();
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  if (user && user.role !== "OWNER") {
    return (
      <div className="app-shell">
        <AppTopBar active="purchase" />
        <main className="content"><div className="alert-error" data-testid="access-denied">Akses ditolak.</div></main>
      </div>
    );
  }

  const paySummary = settlement || (pay ? {
    purchase_total: pay.purchase_total,
    direct_paid: pay.total_paid,
    deposit_allocated: "0",
    total_settled: pay.total_paid,
    outstanding: pay.outstanding,
    payment_status: pay.payment_status,
  } : null);

  return (
    <div className="app-shell">
      <AppTopBar active="purchase" />
      <main className="content">
        <Link to="/purchases" className="back-link" data-testid="back-to-list">← Kembali ke daftar</Link>
        {notice && <div className="alert-ok" data-testid="notice-banner">{notice}</div>}
        {error && <div className="alert-error" data-testid="error-banner">{error}</div>}

        {p && (
          <>
            <section className="panel" data-testid="purchase-detail-panel">
              <div className="panel-head">
                <h2 className="panel-title">Purchase {p.purchase_no}</h2>
                <span className={`status-pill ${p.status === "OPEN" ? "on" : "off"}`} data-testid="purchase-status">{p.status}</span>
              </div>
              <div className="meta-grid">
                <div><span className="meta-label">Purchase No</span><span className="mono">{p.purchase_no}</span></div>
                <div><span className="meta-label">Supplier</span><span data-testid="purchase-supplier">{p.supplier_id ? (supplier?.name || "Supplier tidak ditemukan") : "Belum dipetakan"}</span></div>
                <div><span className="meta-label">Tanggal</span><span>{p.purchase_date}</span></div>
                <div><span className="meta-label">Status</span><span>{p.status}</span></div>
                <div><span className="meta-label">Ongkir Masuk</span><span className="mono" data-testid="inbound-shipping">{formatIDR(p.inbound_shipping_cost)}</span></div>
              </div>

              {p.status === "DRAFT" && (
                <div className="form-actions">
                  <button className="btn-primary" onClick={openPurchase} data-testid="open-purchase-button">Jadikan OPEN</button>
                  <button className="btn-ghost" onClick={() => navigate(`/purchases/${p.id}/edit`)} data-testid="edit-purchase-button">Edit DRAFT</button>
                </div>
              )}
              {p.status !== "DRAFT" && p.status !== "REVERSED" && (
                <div className="form-actions">
                  <button className="btn-ghost" style={{ color: "#b42318", borderColor: "#b42318" }} onClick={openReversal} data-testid="reverse-purchase-button">REVERSAL / BATALKAN TRANSAKSI</button>
                </div>
              )}
            </section>

            {rev.open && (
              <section className="panel" data-testid="reversal-modal" style={{ border: "1px solid #b42318" }}>
                <div className="panel-head"><h2 className="panel-title">Preview Reversal Purchase {p.purchase_no}</h2>
                  <button className="btn-mini" onClick={() => setRev({ open: false, preview: null, reason: "", busy: false })} data-testid="reversal-close">Tutup</button></div>
                {!rev.preview && <p className="hint">Memuat preview…</p>}
                {rev.preview && (
                  <>
                    {rev.preview.blockers.length > 0 && (
                      <div className="alert-error" data-testid="reversal-blockers">
                        <b>Reversal DIBLOKIR:</b>
                        <ul style={{ margin: "6px 0 0 18px" }}>
                          {rev.preview.blockers.map((b) => <li key={b.code} data-testid={`reversal-blocker-${b.code}`}><b>{b.code}</b> — {b.message}{b.corrective ? <div className="hint">Tindakan: {b.corrective}</div> : null}</li>)}
                        </ul>
                      </div>
                    )}
                    <p className="hint" data-testid="reversal-summary">
                      {rev.preview.no_inventory_effect ? "Purchase belum memiliki efek inventory (belum ada receiving): reversal hanya mengunci Purchase sebagai REVERSED." :
                        `Akan dibalik: ${rev.preview.receipts.length} receipt, ${rev.preview.events.length} valuation event, ${rev.preview.movements} stock movement, total qty ${rev.preview.total_qty}, nilai ${formatIDR(rev.preview.total_value)}.`}
                    </p>
                    {rev.preview.events.length > 0 && (
                      <table className="data-table"><thead><tr><th>SKU</th><th>Bucket</th><th>Sumber</th><th>Qty</th><th>Nilai</th></tr></thead>
                        <tbody>{rev.preview.events.map((e) => <tr key={e.id}><td className="mono">{e.sku_code}</td><td>{e.bucket}</td><td>{e.source_type}</td><td className="mono">−{e.qty_effect}</td><td className="mono">−{formatIDR(e.value_effect)}</td></tr>)}</tbody></table>
                    )}
                    {rev.preview.eligible && (
                      <div style={{ marginTop: 10 }}>
                        <label className="field-label">ALASAN REVERSAL (wajib)</label>
                        <textarea className="field-input" rows={2} value={rev.reason} onChange={(e) => setRev({ ...rev, reason: e.target.value })} data-testid="reversal-reason" />
                        <div className="form-actions">
                          <button className="btn-primary" disabled={rev.busy || !rev.reason.trim()} onClick={confirmReversal} data-testid="reversal-confirm">Konfirmasi REVERSE PURCHASE</button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </section>
            )}

            {p.status === "REVERSED" && reversal && (
              <section className="panel" data-testid="reversal-panel" style={{ borderLeft: "4px solid #b42318" }}>
                <div className="panel-head"><h2 className="panel-title">Reversal {reversal.reversal_no}</h2><span className="status-pill off">REVERSED</span></div>
                <div className="meta-grid">
                  <div><span className="meta-label">Alasan</span><span data-testid="reversal-reason-view">{reversal.reason}</span></div>
                  <div><span className="meta-label">Oleh</span><span>{reversal.reversed_by_email || reversal.reversed_by}</span></div>
                  <div><span className="meta-label">Waktu</span><span>{new Date(reversal.reversed_at).toLocaleString("id-ID")}</span></div>
                  <div><span className="meta-label">Status sebelum</span><span>{reversal.status_before}</span></div>
                  <div><span className="meta-label">Reference</span><span className="mono">{reversal.id}</span></div>
                </div>
                <table className="data-table" style={{ marginTop: 8 }}><thead><tr><th>SKU</th><th>Bucket</th><th>Qty</th><th>Nilai</th></tr></thead>
                  <tbody>{reversal.valuation_events.map((e) => <tr key={e.id} data-testid={`reversal-event-${e.sku_code}`}><td className="mono">{e.sku_code}</td><td>{e.bucket}</td><td className="mono">{e.qty_effect}</td><td className="mono">{formatIDR(e.value_effect)}</td></tr>)}</tbody></table>
                <p className="hint">{reversal.movements.length} compensating stock movement.</p>
              </section>
            )}

            <section className="panel" data-testid="purchase-items-panel">
              <h2 className="panel-title">Item SKU</h2>
              <div className="table-scroll">
                <table className="data-table" data-testid="detail-items-table">
                  <thead>
                    <tr>
                      <th>SKU</th><th>Deskripsi</th><th>Ordered</th><th>Accepted</th><th>Cancelled</th>
                      <th>Delivery Outstanding</th><th>Replacement Pending</th><th>Harga Beli / Unit</th><th>Subtotal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {p.items.map((it) => (
                      <tr key={it.id} data-testid={`detail-item-${it.sku_code}`}>
                        <td className="mono">{it.sku_code}<div style={{marginTop:5}}><span data-testid={`purchase-repack-indicator-${it.sku_code}`} title={it.repack_required ? "SKU ditandai wajib Repack" : "SKU tidak ditandai Repack"} style={{display:"inline-block",fontSize:10,fontWeight:700,padding:"3px 7px",borderRadius:999,whiteSpace:"nowrap",background:it.repack_required?"#e5f3eb":"#f1f3f5",color:it.repack_required?"#137344":"#697586"}}>{it.repack_required ? "Masuk Repack" : "Tidak Repack"}</span></div></td>
                        <td>{it.description || "-"}</td>
                        <td className="mono">{it.ordered_qty}</td>
                        <td className="mono" data-testid={`accepted-${it.sku_code}`}>{it.accepted_good_qty ?? 0}{it.bonus_qty ? ` (+${it.bonus_qty} bonus)` : ""}</td>
                        <td className="mono" data-testid={`cancelled-${it.sku_code}`}>{it.cancelled_qty ?? 0}</td>
                        <td className="mono" data-testid={`delivery-outstanding-${it.sku_code}`}>{it.delivery_outstanding_qty ?? it.outstanding_qty ?? it.ordered_qty}{it.has_unresolved_hold ? " ⏳" : ""}</td>
                        <td className="mono" data-testid={`replacement-pending-${it.sku_code}`}>
                          <span>{it.replacement_pending_qty ?? 0}</span>
                          {(it.replacement_pending_qty ?? 0) > 0 && (
                            <button className="btn-secondary btn-xs" style={{ display: "block", marginTop: 4 }}
                              data-testid={`replacement-receive-btn-${it.sku_code}`}
                              onClick={() => setReplForm({ open: true, purchase_item_id: it.id, sku: it.sku_code, max: it.replacement_pending_qty, qty: String(it.replacement_pending_qty) })}>
                              Terima Barang Pengganti
                            </button>
                          )}
                        </td>
                        <td className="mono">{formatIDR(it.purchase_unit_cost)}</td>
                        <td className="mono">{formatIDR(it.subtotal)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot><tr><td colSpan={8} className="total-label">Total Purchase</td><td className="mono total-value" data-testid="purchase-total">{formatIDR(p.purchase_total)}</td></tr></tfoot>
                </table>
              </div>

              {replForm.open && (
                <div className="inline-form" data-testid="replacement-form" style={{ marginTop: 12, padding: 12, border: "1px solid var(--border)", borderRadius: 8 }}>
                  <h3 className="panel-subtitle">Terima Barang Pengganti — {replForm.sku}</h3>
                  <p className="hint">Supplier mengirim barang pengganti untuk unit rusak. GOOD bertambah di harga beli asli; ordered qty & tagihan TIDAK berubah. Maks: {replForm.max}.</p>
                  <div style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap" }}>
                    <div style={{ minWidth: 160 }}><label className="field-label" style={{ whiteSpace: "nowrap" }}>Qty Pengganti</label><input className="field-input" type="number" min="1" max={replForm.max} value={replForm.qty} onChange={(e) => setReplForm({ ...replForm, qty: e.target.value })} data-testid="replacement-qty-input" /></div>
                    <button className="btn-primary" onClick={submitReplacement} data-testid="replacement-submit-btn">Terima</button>
                    <button className="btn-secondary" onClick={() => setReplForm({ open: false, purchase_item_id: "", sku: "", max: 0, qty: "" })} data-testid="replacement-cancel-btn">Batal</button>
                  </div>
                </div>
              )}
            </section>

            {pay && paySummary && p.status !== "DRAFT" && (
              <section className="panel" data-testid="purchase-payment-panel">
                <div className="panel-head">
                  <h2 className="panel-title">Pembayaran</h2>
                  <span className={`status-pill ${paySummary.payment_status === "PAID" ? "on" : "off"}`} data-testid="payment-status">{paySummary.payment_status}</span>
                </div>
                <div className="meta-grid">
                  <div><span className="meta-label">Total Purchase</span><span className="mono" data-testid="pay-total">{formatExactIDR(paySummary.purchase_total)}</span></div>
                  <div><span className="meta-label">Direct Payment</span><span className="mono" data-testid="pay-paid">{formatIDR(paySummary.direct_paid)}</span></div>
                  <div><span className="meta-label">Deposit Terpakai</span><span className="mono" data-testid="pay-deposit-allocated">{formatIDR(paySummary.deposit_allocated)}</span></div>
                  <div><span className="meta-label">Total Settled</span><span className="mono" data-testid="pay-total-settled">{formatIDR(paySummary.total_settled)}</span></div>
                  <div><span className="meta-label">Sisa (Outstanding)</span><span className="mono" data-testid="pay-outstanding">{formatExactIDR(paySummary.outstanding)}</span></div>
                </div>

                {p.status !== "REVERSED" && paySummary.payment_status !== "PAID" && !showPayForm && (
                  <div className="form-actions"><button className="btn-primary" onClick={() => { setPayForm((old) => ({ ...old, amount: String(Math.round(Math.max(0, Number(paySummary.purchase_total || 0) - Number(paySummary.total_settled || 0)))) })); setShowPayForm(true); }} data-testid="record-payment-button">Catat Pembayaran</button></div>
                )}

                {showPayForm && (
                  <div style={{ display: "flex", gap: 8, alignItems: "flex-end", marginTop: 10, flexWrap: "wrap" }} data-testid="payment-form">
                    <div><label className="field-label">Nominal</label><RupiahInput className="field-input narrow" placeholder="jumlah" value={payForm.amount} onChange={(v) => setPayForm({ ...payForm, amount: v })} data-testid="payment-amount-input" /></div>
                    <div><label className="field-label">Sumber Dana → Dari Rekening</label>
                      <select className="field-input" value={payForm.account_id} onChange={(e) => setPayForm({ ...payForm, account_id: e.target.value })} data-testid="payment-account-select" style={{ minWidth: 220 }}>
                        <option value="">-- pilih rekening --</option>
                        {accounts.filter((a) => a.is_active).map((a) => <option key={a.account_id} value={a.account_id}>{a.account_name} ({a.account_type}) — {formatIDR(a.current_balance)}</option>)}
                      </select>
                    </div>
                    <div><label className="field-label">Referensi / Catatan</label><input className="field-input" placeholder="opsional" value={payForm.reference} onChange={(e) => setPayForm({ ...payForm, reference: e.target.value })} data-testid="payment-reference-input" style={{ maxWidth: 220 }} /></div>
                    <button className="btn-primary" onClick={recordPayment} data-testid="payment-submit-button">Simpan</button>
                    <button className="btn-ghost" onClick={() => setShowPayForm(false)} data-testid="payment-cancel-button">Batal</button>
                    <div style={{ flexBasis: "100%", marginTop: 4 }}>
                      <span className="status-pill on" data-testid="payment-fund-identity">IDENTITAS DANA: MODAL</span>
                      {payForm.account_id && (() => { const sel = accounts.find((a) => a.account_id === payForm.account_id); return sel ? <span className="mono" data-testid="payment-modal-available" style={{ marginLeft: 12, fontWeight: 700 }}>DANA MODAL TERSEDIA: {formatIDR(sel.modal_available)}</span> : null; })()}
                    </div>
                  </div>
                )}

                <div className="table-scroll" style={{ marginTop: 12 }}>
                  <table className="data-table" data-testid="payment-history-table">
                    <thead><tr><th>Tanggal</th><th>Account</th><th>Nominal</th><th>Dana</th><th>Status</th><th>Referensi</th><th>Aksi</th></tr></thead>
                    <tbody>
                      {pay.payments.length === 0 && <tr><td colSpan={7} className="empty-cell" data-testid="payments-empty">Belum ada pembayaran.</td></tr>}
                      {pay.payments.map((m) => (
                        <tr key={m.id} data-testid={`payment-row-${m.id}`}>
                          <td>{new Date(m.payment_date).toLocaleDateString("id-ID")}</td><td>{m.account_name || "-"}</td><td className="mono">{formatIDR(m.amount)}</td><td>{m.fund_identity || "MODAL"}</td>
                          <td><span className={`status-pill ${m.status === "POSTED" ? "on" : "off"}`}>{m.status}</span></td><td>{m.reference || "-"}</td>
                          <td>{m.status === "POSTED" && <button className="btn-mini" onClick={() => reversePayment(m.id)} data-testid={`payment-reverse-${m.id}`}>Batalkan</button>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            )}
          </>
        )}
      </main>
    </div>
  );
}
